import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, RouterStateSnapshot, UrlTree } from '@angular/router';
import { AuthService, RETURN_URL_KEY } from '../services/auth.service';
import { AuthStore } from '../../shared/stores/auth.store';
import { AppNavigationService } from '../services/app-navigation.service';
import { PreviewModeService } from '../services/preview-mode.service';
import { SessionHandoffService } from '../services/session-handoff.service';
import { UserStore } from '../../shared/stores/user.store';
import { CustomerStore } from '../../shared/stores/customer.store';
import { PolicyProvisioningStore } from '../../shared/stores/policy-provisioning.store';

/**
 * Protects routes that require an authenticated session.
 * Saves the intended URL to sessionStorage under {@link RETURN_URL_KEY} so that
 * the post-login handler can redirect there. Read by BOTH completion handlers —
 * the credential login form and the SSO callback (Bug 149604) — so the
 * destination survives whichever way the user ends up signing in.
 *
 * After session restore (page reload with a valid token already present) this guard
 * also initialises the proactive token-refresh timer via
 * {@link AuthService.initTimerIfAuthenticated} so that the timer is running even
 * when no login flow was executed in the current page lifecycle.
 *
 * ## Session hand-off (98898 *Authenticated Access*)
 *
 * Tokens live in `sessionStorage`, which is scoped per browser **tab**, so a form link opened in a
 * new tab — from Outlook, Teams, or a pasted URL — arrives with no token and would be sent to login
 * even though the employee is signed in in another tab. Before falling through to login this guard
 * therefore asks sibling tabs of the same origin for the session
 * ({@link SessionHandoffService.requestSession}) and adopts a matching reply.
 *
 * Two properties of that placement are load-bearing and must be preserved:
 *
 * - **Only the unauthenticated branch is asynchronous.** The preview short-circuit and the
 *   authenticated branch stay synchronous, so no navigation that succeeds today gains an `await`,
 *   any latency, or any new behaviour. The bounded wait is paid only where the user would otherwise
 *   have been shown a login form. There are named regression tests for both.
 * - **Every dependency is injected synchronously, at the top.** Nothing may call `inject()` after
 *   the first `await` — the injection context is gone by then. `SessionHandoffService` is injected
 *   unconditionally rather than inside the branch that needs it, because instantiating it is what
 *   makes a tab a live *responder*; a signed-in tab that never injected it could not answer.
 *
 * Apply this guard to the nested route group containing `home`, `form/:id`, and `faq`.
 */
export const authGuard: CanActivateFn = (_route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
  const authService = inject(AuthService);
  const authStore = inject(AuthStore);
  const userStore = inject(UserStore);
  const appNav = inject(AppNavigationService);
  const previewModeService = inject(PreviewModeService);
  const customerStore = inject(CustomerStore);
  const policyProvisioningStore = inject(PolicyProvisioningStore);
  // Injected unconditionally so that EVERY tab passing through a guarded route can answer a
  // sibling's hand-off request — not only the tabs that need to make one.
  const sessionHandoff = inject(SessionHandoffService);

  /**
   * The steps that turn a present, live token into an active session for this tab. Shared by the
   * already-authenticated path and the hand-off path so an adopted tab performs exactly the same
   * steps as a reloaded one — no second, subtly different startup path.
   */
  const grantAccess = (): true => {
    authStore.restoreSession();
    authService.initTimerIfAuthenticated();
    // Restore user profile from sessionStorage if not already loaded.
    if (!userStore.hasProfile()) {
      userStore.restoreFromStorage();
    }
    // Resolve whether this tenant is provisioned for Policy Management so the
    // Policy header tab and Home Policy sub-tab can show/hide. Idempotent — the
    // store skips re-loading once resolved.
    policyProvisioningStore.load(customerStore.customerId());
    return true;
  };

  /** Today's behaviour: remember where the employee was headed, then send them to login. */
  const redirectToLogin = (): UrlTree => {
    sessionStorage.setItem(RETURN_URL_KEY, state.url);
    return appNav.loginUrlTree();
  };

  // Preview mode: no token exists, but the tab was legitimately opened by the admin.
  // Skip auth entirely — the portal config was already loaded by previewGuard (AC#96759-6).
  if (previewModeService.isPreview()) {
    return true;
  }

  if (authService.isAuthenticated) {
    return grantAccess();
  }

  // No token in THIS tab. Before showing a login page, ask sibling tabs whether one of them holds a
  // live session for the customer we are navigating to. The tenant comes from CustomerStore rather
  // than route params because this guard sits on a child route and is invoked with a bare snapshot.
  const tenant = customerStore.customerId();

  // Without a resolved tenant there is nobody to ask and no tenant to match against, so take the
  // redirect SYNCHRONOUSLY — `loginUrlTree()` throws when the customer is unknown, and that throw
  // must stay synchronous to preserve the router's existing error behaviour on this path.
  if (!tenant) {
    return redirectToLogin();
  }

  return (async (): Promise<boolean | UrlTree> => {
    // The hand-off is a best-effort optimisation on a path whose current outcome is already a
    // login page, so any failure inside it must degrade to that login path rather than reject
    // this guard and error the navigation.
    let offer: Awaited<ReturnType<typeof sessionHandoff.requestSession>> = null;
    try {
      offer = await sessionHandoff.requestSession(tenant);
    } catch {
      offer = null;
    }

    // adoptSession re-validates the offered token, so a stale or malformed reply falls through to
    // login rather than producing a half-authenticated tab. On success no return URL is written —
    // the navigation was never abandoned.
    return offer && authService.adoptSession(offer) ? grantAccess() : redirectToLogin();
  })();
};
